import { useCallback, useMemo, useState } from 'react';
import type { AccountUserRole, AccountUserRoleUser } from 'vopay';
import { useApi } from '#shared/providers/Api';
import useUsers from './useUsers';
import unwrap from './unwrap';

// The account's roles, how many users each has, and which users those are. Every fetch rejects on
// failure so the caller decides how to surface it.
export default function useUsersRoles() {
  const api = useApi();
  const { account } = useUsers();
  const [roles, setRoles] = useState<AccountUserRole[]>([]);
  const [fetchingRoles, setFetchingRoles] = useState(false);
  const [fetchingRoleUsers, setFetchingRoleUsers] = useState(false);
  const [usersByRole, setUsersByRole] = useState<Record<number, AccountUserRoleUser[]>>({});

  const fetchRoles = useCallback(async () => {
    try {
      setFetchingRoles(true);
      setRoles((await unwrap(api.fetchAccountUserRoles(account.bearer))).Roles);
    } finally {
      setFetchingRoles(false);
    }
  }, [api, account.bearer]);

  // The backend has no bulk endpoint, so this is one call per role. allSettled keeps one failed role
  // from blanking every tile: a failure counts as an empty role (unwrap has already logged it).
  const fetchRoleUsers = useCallback(
    async (roleIDs: number[]) => {
      try {
        setFetchingRoleUsers(true);

        const settled = await Promise.allSettled(
          roleIDs.map((RoleID) => unwrap(api.fetchUsersByRole({ RoleID }, account.bearer))),
        );

        const nextUsersByRole: Record<number, AccountUserRoleUser[]> = {};

        settled.forEach((result, index) => {
          nextUsersByRole[roleIDs[index]] = result.status === 'fulfilled' ? result.value.Users : [];
        });

        setUsersByRole(nextUsersByRole);
      } finally {
        setFetchingRoleUsers(false);
      }
    },
    [api, account.bearer],
  );

  const roleCounts = useMemo(() => {
    const counts: Record<number, number> = {};

    Object.entries(usersByRole).forEach(([roleID, users]) => {
      counts[Number(roleID)] = users.length;
    });

    return counts;
  }, [usersByRole]);

  // Inverts usersByRole (RoleID → users) into username → roles for the Role(s) column. Keys are lowercased
  // on insert and lookup: usernames are emails, and the two endpoints that supply them may differ in case.
  const rolesByUsername = useMemo(() => {
    const map: Record<string, AccountUserRole[]> = {};

    roles.forEach((role) => {
      (usersByRole[role.RoleID] ?? []).forEach((user) => {
        const key = user.Username.toLowerCase();

        map[key] = [...(map[key] ?? []), role];
      });
    });

    return map;
  }, [roles, usersByRole]);

  return {
    roles,
    fetchingRoles,
    fetchRoles,
    fetchingRoleUsers,
    usersByRole,
    roleCounts,
    fetchRoleUsers,
    rolesByUsername,
  };
}
